/**
 * ConfirmDialog — replaces all window.confirm() calls; AlertDialog wrapper with variant support
 *
 * @example
 * // Imperative usage with useConfirmDialog hook:
 * const confirm = useConfirmDialog();
 * const result = await confirm({
 *   title: t('admin.delete_user_title'),
 *   description: t('admin.delete_user_desc'),
 *   variant: 'danger',
 *   confirmLabel: t('admin.btn_delete'),
 * });
 * if (result) handleDelete();
 *
 * // Declarative usage:
 * <ConfirmDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title={t('admin.delete_user_title')}
 *   description={t('admin.delete_user_desc')}
 *   variant="danger"
 *   onConfirm={handleDelete}
 * />
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── Variant config ───────────────────────────────────────────────────────────
const VARIANTS = {
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-[var(--brand-danger)]',
    confirmClass:
      'bg-[var(--brand-danger)] hover:bg-[var(--brand-danger)]/90 text-white border-[var(--brand-danger-shadow)] border-b-4 rounded-xl font-bold',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-500',
    confirmClass:
      'bg-amber-500 hover:bg-amber-500/90 text-white border-amber-700 border-b-4 rounded-xl font-bold',
  },
  info: {
    icon: Info,
    iconClass: 'text-[var(--brand-primary)]',
    confirmClass:
      'bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white border-[var(--brand-primary-shadow)] border-b-4 rounded-xl font-bold',
  },
  success: {
    icon: CheckCircle,
    iconClass: 'text-[var(--brand-success)]',
    confirmClass:
      'bg-[var(--brand-success)] hover:bg-[var(--brand-success-hover)] text-white border-[var(--brand-success-shadow)] border-b-4 rounded-xl font-bold',
  },
};

// ─── Declarative component ────────────────────────────────────────────────────
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  variant,
  confirmLabel,
  cancelLabel,
  onConfirm,
  loading,
}) {
  const { t } = useLanguage();
  const v = VARIANTS[variant] || VARIANTS.info;
  const Icon = v.icon;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className={cn('mt-0.5', v.iconClass)}>
              <Icon size={22} />
            </div>
            <div>
              <AlertDialogTitle className="font-display text-lg font-bold text-[var(--text-main)]">
                {title}
              </AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="mt-1 text-sm text-[var(--text-muted)]">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 mt-2">
          <AlertDialogCancel
            className="rounded-xl border-2 border-[var(--border-heavy)] font-bold h-9"
            disabled={loading}
          >
            {cancelLabel || t('admin.btn_cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn('h-9', v.confirmClass)}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? t('admin.btn_loading') : (confirmLabel || t('admin.btn_confirm'))}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  variant: PropTypes.oneOf(['danger', 'warning', 'info', 'success']),
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func,
  loading: PropTypes.bool,
};

ConfirmDialog.defaultProps = {
  variant: 'info',
  loading: false,
};

// ─── Imperative hook ──────────────────────────────────────────────────────────
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const resolveRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, ...options });
    });
  }, []);

  const handleConfirm = () => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(true);
  };

  const handleCancel = () => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={state.open}
        onOpenChange={(o) => { if (!o) handleCancel(); }}
        title={state.title || ''}
        description={state.description}
        variant={state.variant}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useConfirmDialog() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirmDialog must be inside ConfirmProvider');
  return ctx;
}
